import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './Trading.module.css';
import compactStyles from './CompactTable.module.css';
import { OptionContractLookupRow, TickL1 } from '../../types/trading';
import { tradingService } from '../../services/TradingService';
import { TickerDropdown } from '../common/TickerDropdown';

/* ── View-model types (frontend-only) ─────────────────────────────── */

type OptionSide = 'call' | 'put';
type TickField = 'bid' | 'ask' | 'last';
type BlinkDirection = 'up' | 'down';

interface ChainRow {
  expiration: string;
  strike: number;
  symbol: string | null;
}

interface BlinkCellState {
  direction: BlinkDirection;
  pulse: number;
}

interface OptionMonitorCacheEntry {
  sessionDateKey: string;
  underlying: string;
  seedContracts: OptionContractLookupRow[];
  expirations: string[];
  byExpiration: Record<string, OptionContractLookupRow[]>;
}

/* ── Constants ─────────────────────────────────────────────────────── */

const DISPLAY_EXPIRATION_COUNT = 5;
const BLINK_DURATION_MS = 600;
const QUOTE_FIELDS: TickField[] = ['bid', 'ask', 'last'];
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MAX_ROWS_PER_EXPIRATION = 10;
const OPTION_MONITOR_CACHE_PREFIX = 'option-monitor-cache:v1:';

const optionMonitorMemoryCache = new Map<string, OptionMonitorCacheEntry>();

/* ── Helpers ───────────────────────────────────────────────────────── */

const formatSessionDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const SESSION_DATE_KEY = formatSessionDateKey(new Date());

const cacheKeyForUnderlying = (underlying: string): string => (
  `${SESSION_DATE_KEY}:${underlying.trim().toUpperCase()}`
);

const storageKeyForCache = (cacheKey: string): string => (
  `${OPTION_MONITOR_CACHE_PREFIX}${cacheKey}`
);

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null
);

const normalizeOptionType = (raw: unknown): OptionSide | null => {
  if (typeof raw !== 'string') return null;
  const optionType = raw.trim().toLowerCase();
  if (optionType === 'call' || optionType === 'c') return 'call';
  if (optionType === 'put' || optionType === 'p') return 'put';
  return null;
};

const normalizeContract = (value: unknown): OptionContractLookupRow | null => {
  if (!isRecord(value)) return null;
  if (typeof value.symbol !== 'string' || !value.symbol.trim()) return null;
  if (typeof value.underlying !== 'string' || !value.underlying.trim()) return null;
  const optionType = normalizeOptionType(value.option_type);
  if (!optionType) return null;
  if (typeof value.strike !== 'number' || !Number.isFinite(value.strike)) return null;
  if (typeof value.expiration !== 'string' || !value.expiration.trim()) return null;
  const expiration = value.expiration.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(expiration)) return null;

  return {
    symbol: value.symbol.trim().toUpperCase(),
    underlying: value.underlying.trim().toUpperCase(),
    option_type: optionType,
    strike: value.strike,
    expiration,
  };
};

const normalizeCacheEntry = (value: unknown): OptionMonitorCacheEntry | null => {
  if (!isRecord(value)) return null;
  if (typeof value.sessionDateKey !== 'string') return null;
  if (typeof value.underlying !== 'string' || !value.underlying.trim()) return null;
  if (!Array.isArray(value.seedContracts)) return null;
  if (!Array.isArray(value.expirations)) return null;
  if (!isRecord(value.byExpiration)) return null;

  const seedContracts = value.seedContracts
    .map((entry) => normalizeContract(entry))
    .filter((entry): entry is OptionContractLookupRow => Boolean(entry));
  const expirations = value.expirations
    .filter((entry): entry is string => typeof entry === 'string' && Boolean(entry.trim()))
    .sort();

  const byExpiration: Record<string, OptionContractLookupRow[]> = {};
  Object.entries(value.byExpiration).forEach(([expiration, contracts]) => {
    if (!Array.isArray(contracts)) return;
    byExpiration[expiration] = contracts
      .map((entry) => normalizeContract(entry))
      .filter((entry): entry is OptionContractLookupRow => Boolean(entry));
  });

  return {
    sessionDateKey: value.sessionDateKey,
    underlying: value.underlying.trim().toUpperCase(),
    seedContracts,
    expirations,
    byExpiration,
  };
};

const readCacheEntry = (cacheKey: string): OptionMonitorCacheEntry | null => {
  const fromMemory = optionMonitorMemoryCache.get(cacheKey);
  if (fromMemory) return fromMemory;

  try {
    const raw = sessionStorage.getItem(storageKeyForCache(cacheKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const normalized = normalizeCacheEntry(parsed);
    if (!normalized) return null;
    optionMonitorMemoryCache.set(cacheKey, normalized);
    return normalized;
  } catch {
    return null;
  }
};

const writeCacheEntry = (cacheKey: string, entry: OptionMonitorCacheEntry): void => {
  optionMonitorMemoryCache.set(cacheKey, entry);
  try {
    sessionStorage.setItem(storageKeyForCache(cacheKey), JSON.stringify(entry));
  } catch {
    // Ignore session storage quota/runtime failures.
  }
};

const mergeContracts = (
  ...contractGroups: OptionContractLookupRow[][]
): OptionContractLookupRow[] => {
  const deduped = new Map<string, OptionContractLookupRow>();
  contractGroups.flat().forEach((contract) => {
    const normalized = normalizeContract(contract);
    if (!normalized) return;
    if (!deduped.has(normalized.symbol)) {
      deduped.set(normalized.symbol, normalized);
    }
  });

  return Array.from(deduped.values()).sort((left, right) => (
    left.expiration.localeCompare(right.expiration) ||
    left.strike - right.strike ||
    left.symbol.localeCompare(right.symbol)
  ));
};

const uniqueSortedExpirations = (contracts: OptionContractLookupRow[]): string[] => (
  Array.from(new Set(contracts.map((contract) => contract.expiration))).sort()
);

const groupContractsByExpiration = (
  contracts: OptionContractLookupRow[],
): Record<string, OptionContractLookupRow[]> => {
  const grouped: Record<string, OptionContractLookupRow[]> = {};
  contracts.forEach((contract) => {
    const normalized = normalizeContract(contract);
    if (!normalized) return;
    if (!grouped[normalized.expiration]) {
      grouped[normalized.expiration] = [];
    }
    grouped[normalized.expiration].push(normalized);
  });

  Object.entries(grouped).forEach(([expiration, rows]) => {
    grouped[expiration] = mergeContracts(rows);
  });

  return grouped;
};

const strikeKey = (strike: number): number => Math.round(strike * 1000);

const uniqueSortedStrikes = (contracts: OptionContractLookupRow[]): number[] => {
  const deduped = new Map<number, number>();
  contracts.forEach((contract) => {
    deduped.set(strikeKey(contract.strike), contract.strike);
  });

  return Array.from(deduped.values()).sort((left, right) => left - right);
};

const centeredStrikeWindow = (
  strikes: number[],
  centerStrike: number,
  rowCount: number,
): number[] => {
  if (strikes.length === 0) return [];
  const desiredCount = Math.max(1, Math.min(rowCount, strikes.length));
  const exactIndex = strikes.findIndex((strike) => strikeKey(strike) === strikeKey(centerStrike));
  const selected: number[] = [];

  let lower = -1;
  let upper = strikes.length;

  if (exactIndex >= 0) {
    selected.push(strikes[exactIndex]);
    lower = exactIndex - 1;
    upper = exactIndex + 1;
  } else {
    upper = strikes.findIndex((strike) => strike > centerStrike);
    if (upper < 0) upper = strikes.length;
    lower = upper - 1;

    // If center strike does not exist, start from the first strike above it.
    if (upper < strikes.length) {
      selected.push(strikes[upper]);
      upper += 1;
    }
    if (selected.length < desiredCount && lower >= 0) {
      selected.push(strikes[lower]);
      lower -= 1;
    }
  }

  while (selected.length < desiredCount && (lower >= 0 || upper < strikes.length)) {
    if (upper < strikes.length && lower >= 0) {
      const upperDistance = Math.abs(strikes[upper] - centerStrike);
      const lowerDistance = Math.abs(centerStrike - strikes[lower]);
      if (upperDistance <= lowerDistance) {
        selected.push(strikes[upper]);
        upper += 1;
      } else {
        selected.push(strikes[lower]);
        lower -= 1;
      }
      continue;
    }

    if (upper < strikes.length) {
      selected.push(strikes[upper]);
      upper += 1;
    } else if (lower >= 0) {
      selected.push(strikes[lower]);
      lower -= 1;
    }
  }

  return selected.sort((left, right) => left - right);
};

const buildRowsForSide = (
  contracts: OptionContractLookupRow[],
  side: OptionSide,
  expiration: string,
  strikes: number[],
): ChainRow[] => {
  const symbolByStrike = new Map<number, string>();
  contracts
    .filter((contract) => contract.option_type === side)
    .forEach((contract) => {
      const key = strikeKey(contract.strike);
      if (!symbolByStrike.has(key)) {
        symbolByStrike.set(key, contract.symbol);
      }
    });

  return strikes.map((strike) => ({
    expiration,
    strike,
    symbol: symbolByStrike.get(strikeKey(strike)) ?? null,
  }));
};

const formatExpirationOffset = (expiration: string): string => {
  const expiry = new Date(`${expiration}T00:00:00Z`);
  if (Number.isNaN(expiry.getTime())) {
    return '--';
  }

  const now = new Date();
  const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const expiryUtc = Date.UTC(expiry.getUTCFullYear(), expiry.getUTCMonth(), expiry.getUTCDate());
  const days = Math.max(0, Math.round((expiryUtc - todayUtc) / MS_PER_DAY));
  return `${days}D`;
};

const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatExpirationLabel = (expiration: string): { daysTag: string; dayName: string; monthDay: string } => {
  const expiry = new Date(`${expiration}T00:00:00Z`);
  if (Number.isNaN(expiry.getTime())) {
    return { daysTag: '--', dayName: '', monthDay: expiration };
  }
  const daysTag = formatExpirationOffset(expiration);
  const dayName = SHORT_DAYS[expiry.getUTCDay()];
  const monthDay = `${SHORT_MONTHS[expiry.getUTCMonth()]} ${expiry.getUTCDate()}`;
  return { daysTag, dayName, monthDay };
};

const formatPrice = (v: number | null | undefined): string =>
  v == null ? '--' : v.toFixed(2);

const formatTs = (ts?: string): string => {
  if (!ts) return '--';
  try {
    return new Date(ts).toLocaleTimeString();
  } catch {
    return '--';
  }
};

const getCellKey = (symbol: string, field: TickField): string => `${symbol}:${field}`;

const isFiniteNumber = (value: number | null | undefined): value is number => (
  typeof value === 'number' && Number.isFinite(value)
);

export const __resetOptionMonitorCacheForTests = (): void => {
  optionMonitorMemoryCache.clear();
  try {
    const keys: string[] = [];
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (key && key.startsWith(OPTION_MONITOR_CACHE_PREFIX)) {
        keys.push(key);
      }
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Ignore storage errors in non-browser contexts.
  }
};

/* ── Props ─────────────────────────────────────────────────────────── */

export interface OptionChainExplorerProps {
  ticksBySymbol: Record<string, TickL1>;
  onSubscribe: (symbol: string) => void;
  onUnsubscribe: (symbol: string) => void;
  onSelectSymbol: (symbol: string) => void;
}

/* ── Component ─────────────────────────────────────────────────────── */

export const OptionChainExplorer: React.FC<OptionChainExplorerProps> = ({
  ticksBySymbol,
  onSubscribe,
  onUnsubscribe,
  onSelectSymbol,
}) => {
  const [underlying, setUnderlying] = useState('AAPL');
  const [centerStrike, setCenterStrike] = useState<number | null>(null);
  const [startingExpiration, setStartingExpiration] = useState<string>('');
  const [allExpirations, setAllExpirations] = useState<string[]>([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [contractsError, setContractsError] = useState<string | null>(null);

  const [contractsByExpiration, setContractsByExpiration] = useState<Record<string, OptionContractLookupRow[]>>({});

  const [blinkCells, setBlinkCells] = useState<Record<string, BlinkCellState>>({});
  const [expandedExpirations, setExpandedExpirations] = useState<Set<string>>(new Set());
  const prevSubsRef = useRef<Set<string>>(new Set());
  const previousValuesRef = useRef<Record<string, number | null | undefined>>({});
  const timersRef = useRef<Record<string, number>>({});
  const activeCacheKeyRef = useRef<string>('');

  const normalizedUnderlying = underlying.trim().toUpperCase();

  useEffect(() => {
    if (!normalizedUnderlying) {
      setCenterStrike(null);
      return;
    }

    let cancelled = false;
    tradingService
      .getLatestClose('stock', [normalizedUnderlying])
      .then((response) => {
        if (cancelled) return;
        const entry = response.values[normalizedUnderlying];
        if (entry?.latest_close != null && Number.isFinite(entry.latest_close)) {
          setCenterStrike(Math.ceil(entry.latest_close));
        }
      })
      .catch(() => {
        // Leave centerStrike as null; the chain will still render without a center.
      });

    return () => { cancelled = true; };
  }, [normalizedUnderlying]);

  const loadSeedContracts = useCallback(async () => {
    if (!normalizedUnderlying) return;
    const cacheKey = cacheKeyForUnderlying(normalizedUnderlying);
    activeCacheKeyRef.current = cacheKey;

    const cached = readCacheEntry(cacheKey);
    if (cached) {
      const cachedByExpiration =
        Object.keys(cached.byExpiration).length > 0
          ? cached.byExpiration
          : groupContractsByExpiration(cached.seedContracts);
      const hasAnyCachedContracts = Object.values(cachedByExpiration).some(
        (contracts) => contracts.length > 0,
      );
      if (
        Object.keys(cached.byExpiration).length === 0 &&
        Object.keys(cachedByExpiration).length > 0
      ) {
        writeCacheEntry(cacheKey, {
          ...cached,
          byExpiration: cachedByExpiration,
        });
      }

      if (cached.expirations.length > 0 || hasAnyCachedContracts) {
        setAllExpirations(cached.expirations);
        setContractsByExpiration(cachedByExpiration);
        setContractsError(null);
        setLoadingContracts(false);
        return;
      }
    }

    setLoadingContracts(true);
    setContractsError(null);

    try {
      const [calls, puts] = await Promise.all([
        tradingService.listOptionContractsByType(normalizedUnderlying, 'call'),
        tradingService.listOptionContractsByType(normalizedUnderlying, 'put'),
      ]);

      if (activeCacheKeyRef.current !== cacheKey) return;

      const seedContracts = mergeContracts(calls, puts);
      const expirations = uniqueSortedExpirations(seedContracts);
      const byExpiration = groupContractsByExpiration(seedContracts);
      const nextEntry: OptionMonitorCacheEntry = {
        sessionDateKey: SESSION_DATE_KEY,
        underlying: normalizedUnderlying,
        seedContracts,
        expirations,
        byExpiration,
      };

      writeCacheEntry(cacheKey, nextEntry);
      setAllExpirations(expirations);
      setContractsByExpiration(byExpiration);
      if (expirations.length === 0) {
        setContractsError(`No option contracts found for ${normalizedUnderlying}`);
      }
    } catch (err: any) {
      if (activeCacheKeyRef.current !== cacheKey) return;
      setContractsError(err?.message || 'Failed to load contracts');
      setAllExpirations([]);
      setContractsByExpiration({});
    } finally {
      if (activeCacheKeyRef.current === cacheKey) {
        setLoadingContracts(false);
      }
    }
  }, [normalizedUnderlying]);

  useEffect(() => {
    if (!normalizedUnderlying) {
      setAllExpirations([]);
      setStartingExpiration('');
      setContractsByExpiration({});
      return;
    }

    setExpandedExpirations(new Set());
    setStartingExpiration('');
    setAllExpirations([]);
    setContractsByExpiration({});
    setLoadingContracts(false);
    void loadSeedContracts();
  }, [normalizedUnderlying, loadSeedContracts]);

  useEffect(() => {
    if (allExpirations.length === 0) {
      setStartingExpiration('');
      return;
    }

    if (!startingExpiration || !allExpirations.includes(startingExpiration)) {
      setStartingExpiration(allExpirations[0]);
    }
  }, [allExpirations, startingExpiration]);

  const windowedExpirations = useMemo(() => {
    if (allExpirations.length === 0) return [];
    const startIndex = Math.max(0, allExpirations.indexOf(startingExpiration));
    return allExpirations.slice(startIndex, startIndex + DISPLAY_EXPIRATION_COUNT);
  }, [allExpirations, startingExpiration]);

  useEffect(() => {
    setExpandedExpirations((prev) => {
      const next = new Set<string>();
      prev.forEach((expiration) => {
        if (windowedExpirations.includes(expiration)) {
          next.add(expiration);
        }
      });
      if (next.size === 0 && windowedExpirations.length > 0) {
        next.add(windowedExpirations[0]);
      }
      return next;
    });
  }, [windowedExpirations]);

  const strikeWindowByExpiration = useMemo(() => {
    const next: Record<string, number[]> = {};
    windowedExpirations.forEach((expiration) => {
      const contracts = contractsByExpiration[expiration] ?? [];
      const allStrikes = uniqueSortedStrikes(contracts);
      const effectiveCenter = centerStrike ?? (allStrikes.length > 0 ? allStrikes[Math.floor(allStrikes.length / 2)] : 0);
      next[expiration] = centeredStrikeWindow(
        allStrikes,
        effectiveCenter,
        MAX_ROWS_PER_EXPIRATION,
      );
    });
    return next;
  }, [windowedExpirations, contractsByExpiration, centerStrike]);

  const callGroups = useMemo(() => (
    windowedExpirations.map((expiration) =>
      buildRowsForSide(
        contractsByExpiration[expiration] ?? [],
        'call',
        expiration,
        strikeWindowByExpiration[expiration] ?? [],
      ))
  ), [windowedExpirations, contractsByExpiration, strikeWindowByExpiration]);

  const putGroups = useMemo(() => (
    windowedExpirations.map((expiration) =>
      buildRowsForSide(
        contractsByExpiration[expiration] ?? [],
        'put',
        expiration,
        strikeWindowByExpiration[expiration] ?? [],
      ))
  ), [windowedExpirations, contractsByExpiration, strikeWindowByExpiration]);

  const allFlatRows = useMemo(
    () => [...callGroups.flat(), ...putGroups.flat()],
    [callGroups, putGroups],
  );

  const toggleExpiration = useCallback((expiration: string) => {
    setExpandedExpirations((prev) => {
      const wasOpen = prev.has(expiration);
      if (wasOpen) {
        return new Set<string>();
      }
      return new Set<string>([expiration]);
    });
  }, []);

  const currentVisibleSubs = useMemo(() => {
    const subs = new Set<string>();
    for (const row of allFlatRows) {
      if (!expandedExpirations.has(row.expiration)) continue;
      if (!row.symbol) continue;
      subs.add(row.symbol);
    }
    return subs;
  }, [allFlatRows, expandedExpirations]);

  useEffect(() => {
    const prev = prevSubsRef.current;
    currentVisibleSubs.forEach((sym) => {
      if (!prev.has(sym)) onSubscribe(sym);
    });
    prev.forEach((sym) => {
      if (!currentVisibleSubs.has(sym)) onUnsubscribe(sym);
    });
    prevSubsRef.current = new Set(currentVisibleSubs);
  }, [currentVisibleSubs, onSubscribe, onUnsubscribe]);

  useEffect(() => () => {
    prevSubsRef.current.forEach((sym) => onUnsubscribe(sym));
    prevSubsRef.current.clear();
  }, [onUnsubscribe]);

  useEffect(() => {
    const firstReal = allFlatRows.find(
      (row) => Boolean(row.symbol) && expandedExpirations.has(row.expiration),
    );
    if (firstReal?.symbol) {
      onSelectSymbol(firstReal.symbol);
    }
  }, [allFlatRows, expandedExpirations, onSelectSymbol]);

  useEffect(() => {
    const changedCells: Array<{ cellKey: string; direction: BlinkDirection }> = [];

    for (const row of allFlatRows) {
      if (!row.symbol) continue;
      const tick = ticksBySymbol[row.symbol];

      QUOTE_FIELDS.forEach((field) => {
        const cellKey = getCellKey(row.symbol as string, field);
        const previous = previousValuesRef.current[cellKey];
        const current = tick?.[field];

        if (isFiniteNumber(previous) && isFiniteNumber(current) && current !== previous) {
          changedCells.push({
            cellKey,
            direction: current > previous ? 'up' : 'down',
          });
        }

        previousValuesRef.current[cellKey] = current;
      });
    }

    if (changedCells.length === 0) return;

    setBlinkCells((prev) => {
      const next = { ...prev };

      changedCells.forEach(({ cellKey, direction }) => {
        const pulse = (prev[cellKey]?.pulse ?? 0) + 1;
        next[cellKey] = { direction, pulse };

        const existingTimer = timersRef.current[cellKey];
        if (existingTimer !== undefined) {
          window.clearTimeout(existingTimer);
        }

        timersRef.current[cellKey] = window.setTimeout(() => {
          setBlinkCells((currentBlinkCells) => {
            const active = currentBlinkCells[cellKey];
            if (!active || active.pulse !== pulse) {
              return currentBlinkCells;
            }

            const updated = { ...currentBlinkCells };
            delete updated[cellKey];
            return updated;
          });
          delete timersRef.current[cellKey];
        }, BLINK_DURATION_MS);
      });

      return next;
    });
  }, [allFlatRows, ticksBySymbol]);

  useEffect(() => () => {
    Object.values(timersRef.current).forEach((timerId) => window.clearTimeout(timerId));
    timersRef.current = {};
  }, []);

  const cellClassName = (symbol: string | null, field: TickField): string => {
    if (!symbol) return styles.quoteCell;
    const blink = blinkCells[getCellKey(symbol, field)];
    if (!blink) return styles.quoteCell;
    if (blink.direction === 'up') return `${styles.quoteCell} ${styles.quoteBlinkUp}`;
    return `${styles.quoteCell} ${styles.quoteBlinkDown}`;
  };

  const cellPulse = (symbol: string | null, field: TickField): number => {
    if (!symbol) return 0;
    return blinkCells[getCellKey(symbol, field)]?.pulse ?? 0;
  };

  const renderUnifiedRow = (
    callRow: ChainRow | undefined,
    putRow: ChainRow | undefined,
    strike: number,
    expiration: string,
  ) => {
    const callTick = callRow?.symbol ? ticksBySymbol[callRow.symbol] : undefined;
    const putTick = putRow?.symbol ? ticksBySymbol[putRow.symbol] : undefined;
    const callItm = centerStrike != null && strike < centerStrike;
    const putItm = centerStrike != null && strike > centerStrike;

    return (
      <tr
        key={`${expiration}-${strike}`}
        className={styles.chainSubRow}
        data-testid="option-monitor-calls-subrow"
        data-expiration={expiration}
        data-strike={strike.toFixed(3)}
      >
        {/* Calls side: Bid, Ask, Last */}
        <td
          key={`cbid-${cellPulse(callRow?.symbol ?? null, 'bid')}`}
          className={`${compactStyles.bidCell} ${cellClassName(callRow?.symbol ?? null, 'bid')}${callItm ? ` ${styles.itm}` : ''}`}
        >
          {formatPrice(callTick?.bid)}
        </td>
        <td
          key={`cask-${cellPulse(callRow?.symbol ?? null, 'ask')}`}
          className={`${compactStyles.askCell} ${cellClassName(callRow?.symbol ?? null, 'ask')}${callItm ? ` ${styles.itm}` : ''}`}
        >
          {formatPrice(callTick?.ask)}
        </td>
        <td
          key={`clast-${cellPulse(callRow?.symbol ?? null, 'last')}`}
          className={`${cellClassName(callRow?.symbol ?? null, 'last')}${callItm ? ` ${styles.itm}` : ''}`}
        >
          {formatPrice(callTick?.last)}
        </td>

        {/* Strike center */}
        <td className={styles.strikeCell}>{strike.toFixed(2)}</td>

        {/* Puts side: Bid, Ask, Last */}
        <td
          key={`pbid-${cellPulse(putRow?.symbol ?? null, 'bid')}`}
          className={`${compactStyles.bidCell} ${cellClassName(putRow?.symbol ?? null, 'bid')}${putItm ? ` ${styles.itm}` : ''}`}
        >
          {formatPrice(putTick?.bid)}
        </td>
        <td
          key={`pask-${cellPulse(putRow?.symbol ?? null, 'ask')}`}
          className={`${compactStyles.askCell} ${cellClassName(putRow?.symbol ?? null, 'ask')}${putItm ? ` ${styles.itm}` : ''}`}
        >
          {formatPrice(putTick?.ask)}
        </td>
        <td
          key={`plast-${cellPulse(putRow?.symbol ?? null, 'last')}`}
          className={`${cellClassName(putRow?.symbol ?? null, 'last')}${putItm ? ` ${styles.itm}` : ''}`}
        >
          {formatPrice(putTick?.last)}
        </td>
      </tr>
    );
  };

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>Option Monitor</header>

      <div className={styles.inlineForm} style={{ flexWrap: 'wrap' }}>
        <TickerDropdown
          value={underlying}
          onChange={setUnderlying}
          onCommit={setUnderlying}
          placeholder="Select symbol"
          allowClear={false}
          className={styles.chainSymbolDropdown}
        />
        <input
          className={styles.input}
          type="number"
          value={centerStrike ?? ''}
          data-testid="option-monitor-center-strike-input"
          onChange={(e) => setCenterStrike(Number(e.target.value) || 0)}
          placeholder="Center Strike"
          title="Centered Strike"
          style={{ maxWidth: 110, textAlign: 'center' }}
        />
        <select
          className={styles.select}
          value={startingExpiration}
          data-testid="option-monitor-starting-expiration-select"
          onChange={(e) => setStartingExpiration(e.target.value)}
          style={{ maxWidth: 160 }}
          title="Starting Expiration"
          disabled={allExpirations.length === 0}
        >
          {allExpirations.map((exp) => (
            <option key={exp} value={exp}>
              {exp} ({formatExpirationOffset(exp)})
            </option>
          ))}
        </select>
      </div>

      {contractsError && <div className={styles.error}>{contractsError}</div>}

      <div className={styles.tableWrap}>
        <table className={compactStyles.compactTable}>
          <thead>
            <tr>
              <th colSpan={3} style={{ textAlign: 'center' }}>Calls</th>
              <th style={{ textAlign: 'center' }}>Strike</th>
              <th colSpan={3} style={{ textAlign: 'center' }}>Puts</th>
            </tr>
            <tr>
              <th>Bid</th>
              <th>Ask</th>
              <th>Last</th>
              <th></th>
              <th>Bid</th>
              <th>Ask</th>
              <th>Last</th>
            </tr>
          </thead>
          <tbody>
            {windowedExpirations.map((expiration, index) => {
              const callGroup = callGroups[index] ?? [];
              const putGroup = putGroups[index] ?? [];
              const isOpen = expandedExpirations.has(expiration);
              const hasAnyExpanded = expandedExpirations.size > 0;
              const strikes = strikeWindowByExpiration[expiration] ?? [];

              // Legend behavior: when any expiration is expanded, hide all others
              if (hasAnyExpanded && !isOpen) return null;

              const effectiveCenter = centerStrike ?? 0;
              const strikesAbove = centerStrike != null ? strikes.filter((s) => s < effectiveCenter) : [];
              const strikesBelow = centerStrike != null ? strikes.filter((s) => s >= effectiveCenter) : strikes;

              return (
                <React.Fragment key={expiration}>
                  <tr
                    className={`${styles.chainParentRow}${isOpen ? ` ${styles.chainParentOpen}` : ''}`}
                    onClick={() => toggleExpiration(expiration)}
                    data-testid="option-monitor-calls-expiration-row"
                    data-expiration={expiration}
                  >
                    <td colSpan={7}>
                      <span className={styles.chainExpander}>{isOpen ? '▼' : '▶'}</span>
                      {(() => {
                        const label = formatExpirationLabel(expiration);
                        return (
                          <span className={styles.chainExpLabel} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '4px', padding: '2px 6px', fontSize: '0.72rem', fontWeight: 700 }}>{label.daysTag}</span>
                            <span>{label.dayName}</span>
                            <span>{label.monthDay}</span>
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                  {isOpen && strikes.length === 0 && (
                    <tr className={styles.chainSubRow}>
                      <td colSpan={7} className={styles.emptyCell}>Not available</td>
                    </tr>
                  )}
                  {isOpen && strikesAbove.map((strike) => {
                    const callRow = callGroup.find((r) => strikeKey(r.strike) === strikeKey(strike));
                    const putRow = putGroup.find((r) => strikeKey(r.strike) === strikeKey(strike));
                    return renderUnifiedRow(callRow, putRow, strike, expiration);
                  })}
                  {isOpen && strikes.length > 0 && centerStrike != null && (
                    <tr className={styles.strikeSeparator}>
                      <td colSpan={3}></td>
                      <td style={{ textAlign: 'center', padding: '6px 0' }}>
                        <span className={styles.strikeSeparatorLabel}>${centerStrike.toFixed(2)}</span>
                      </td>
                      <td colSpan={3}></td>
                    </tr>
                  )}
                  {isOpen && strikesBelow.map((strike) => {
                    const callRow = callGroup.find((r) => strikeKey(r.strike) === strikeKey(strike));
                    const putRow = putGroup.find((r) => strikeKey(r.strike) === strikeKey(strike));
                    return renderUnifiedRow(callRow, putRow, strike, expiration);
                  })}
                </React.Fragment>
              );
            })}
            {windowedExpirations.length === 0 && (
              <tr>
                <td colSpan={7} className={styles.emptyCell}>
                  {loadingContracts ? 'Loading contracts…' : 'Not available'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};
